import { TypedDocumentNode, gql } from '@apollo/client';
import { Navigate, useParams } from 'react-router-dom';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { FOOTBALL_MARKET_MANAGER_SALES } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { createCardSorts } from '@sorare/core/src/lib/algolia';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { MarketStackTitle } from '@sorare/marketplace/src/components/market/MarketStackTitle';
import { PageTemplate } from '@sorare/marketplace/src/components/market/PageTemplate';
import { useDefaultFilters } from '@sorare/marketplace/src/hooks/useDefaultFilters';
import { AdvancedCardSearch } from '@sorare/marketplace/src/searchCards';
import { secondaryMarketStackedFilters } from '@sorare/marketplace/src/searchCards/football/cardFilters';

import type {
  So5SecondaryMarketStackPlayerQuery,
  So5SecondaryMarketStackPlayerQueryVariables,
} from './__generated__/page.graphql';

const SO5_SECONDARY_MARKET_STACK_PLAYER_QUERY = gql`
  query So5SecondaryMarketStackPlayerQuery($slug: String!) {
    football {
      player(slug: $slug) {
        slug
        displayName
      }
    }
  }
` as TypedDocumentNode<
  So5SecondaryMarketStackPlayerQuery,
  So5SecondaryMarketStackPlayerQueryVariables
>;

const TransferMarketStackPage = () => {
  const { playerSlug, rarity } = useParams();
  const { data, loading } = useQuery(SO5_SECONDARY_MARKET_STACK_PLAYER_QUERY, {
    variables: {
      slug: playerSlug!,
    },
    skip: !playerSlug,
  });

  useTitleAndDescription(
    metadatas.transfers.title,
    metadatas.transfers.description
  );

  const cardSortsWithBestValue = createCardSorts({
    indexPrefix: 'Cards On Sale',
    withBestValue: true,
    withHighestPrice: true,
    withEndingSoon: false,
  });

  const defaultSort = 'Cards On Sale Lowest Price';

  const filters = useDefaultFilters({
    secondary: true,
    rarity,
    playerSlug,
    onlySettlableCards: true,
  });

  if (loading) {
    return <LoadingIndicator />;
  }

  if (
    !data?.football.player ||
    !rarity ||
    !Object.keys(Rarity).includes(rarity)
  ) {
    return <Navigate to={FOOTBALL_MARKET_MANAGER_SALES} />;
  }

  return (
    <PageTemplate showBack>
      <Portal id="page-header-title-breadcrumb">
        <MarketStackTitle
          player={data.football.player}
          rarity={rarity as Rarity}
          sport={Sport.FOOTBALL}
        />
      </Portal>
      <AdvancedCardSearch
        sport={Sport.FOOTBALL}
        cardFilters={secondaryMarketStackedFilters}
        analyticsTags={['TransferMarketStack', 'Football']}
        sorts={cardSortsWithBestValue}
        defaultSort={defaultSort}
        defaultFilters={filters}
        removeEndedSingleSaleOffers
        topic={{
          type: 'stack',
          label: data.football.player.displayName,
        }}
        distinct={false}
      />
    </PageTemplate>
  );
};

export default TransferMarketStackPage;
